// web/src/routes/auth/ResetPassword.jsx
// Public page: lands from the reset email at /auth/reset?token=...
// NOTE: adjust the `api` import to match how your other routes import the wrapper
// (e.g. '../../lib/api'). It needs api.post(path, body).

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import Field from '../../components/ui/Field.jsx';

// This page uses the light .card look rather than the forest auth panel, so the
// shared Field gets the global .input class instead of its default styling.
// py-3 (over .input's py-2) keeps the reveal toggle a 44px+ tap target.
const RESET_INPUT = 'input w-full py-3';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState('idle'); // idle | saving | done
  const [error, setError] = useState(null);

  async function submit() {
    setError(null);
    if (password.length < 10) {
      setError('Password must be at least 10 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setState('saving');
    try {
      await api.post('/auth/reset-password', { token, password });
      setState('done');
      setTimeout(() => navigate('/login'), 1500);
    } catch {
      setError('This reset link is invalid or has expired. Request a new one.');
      setState('idle');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a2b1f] px-4">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-xl font-semibold mb-2 text-ink-900">Set a new password</h1>

        {!token ? (
          <>
            <p className="text-ink-600 mb-6">
              This reset link is missing its token. Request a new link to continue.
            </p>
            <Link to="/auth/forgot" className="btn-primary w-full text-center block">
              Request a reset link
            </Link>
          </>
        ) : state === 'done' ? (
          <p className="text-ink-600">
            Password updated. Taking you to sign in…
          </p>
        ) : (
          <>
            <Field
              type="password"
              autoComplete="new-password"
              placeholder="New password (10+ characters)"
              value={password}
              onChange={setPassword}
              reveal
              inputClassName={RESET_INPUT}
              wrapperClassName="relative mb-3"
            />
            <Field
              type="password"
              autoComplete="new-password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              reveal
              inputClassName={RESET_INPUT}
              wrapperClassName="relative mb-3"
            />
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <button
              className="btn-primary w-full"
              disabled={state === 'saving'}
              onClick={submit}
            >
              {state === 'saving' ? 'Saving…' : 'Update Password'}
            </button>
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-ink-500 underline">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
